/**
 * DOC 8 §2/§3/§4 — P&ID interactive layer.
 *
 *  attachPidInteractive(host, { onOpcNavigate })
 *      → hover tooltip that reads data-* attributes and shows an
 *        engineering property panel
 *      → tag search (Ctrl+F) with wildcard / type filter
 *      → OPC click navigation (fires onOpcNavigate callback)
 *      → returns cleanup function to remove all listeners + DOM nodes
 *
 * Consumers: <InteractivePidPanel /> and <PidEditor /> — call this
 * once per SVG mount and dispose on unmount.
 */

const TOOLTIP_ID = "pid-tooltip-doc8";
const SEARCHBOX_ID = "pid-searchbox-doc8";

/* ────────────────────────────────────────────────────────
   Tooltip
   ──────────────────────────────────────────────────────── */

function _createTooltip() {
   let el = document.getElementById(TOOLTIP_ID);
   if (el) return el;
   el = document.createElement("div");
   el.id = TOOLTIP_ID;
   el.setAttribute("data-testid", "pid-tooltip");
   el.style.cssText = [
      "position:absolute", "background:#fff", "border:1px solid #333",
      "border-radius:4px", "padding:8px 12px",
      "font:12px 'Segoe UI',Arial,sans-serif",
      "max-width:340px", "box-shadow:0 2px 10px rgba(0,0,0,0.18)",
      "pointer-events:none", "display:none", "z-index:9999",
   ].join(";");
   document.body.appendChild(el);
   return el;
}

function _formatKey(key) {
   /* dataDesignPressure → "Design Pressure"  */
   return key
      .replace(/^data-/, "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
}

function _renderTooltip(el, group) {
   const tag = group.dataset.tag ||
                group.dataset.lineNumber || group.dataset.elemId || "";
   const type = group.dataset.type || group.dataset.elemSubtype || "";
   const service = group.dataset.service || "";
   const skip = new Set(["tag", "type", "service",
                              "elemId", "elemType", "elemSubtype",
                              "elemPipe", "elemLoop"]);
   let html = `<div data-testid="pid-tooltip-title" style="font-weight:600;font-size:14px;
                    border-bottom:1px solid #ddd;padding-bottom:4px;
                    margin-bottom:6px">${tag}</div>`;
   if (type)     html += `<div><b>Type:</b> ${type.replace(/_/g, " ")}</div>`;
   if (service)  html += `<div><b>Service:</b> ${service}</div>`;
   for (const [k, v] of Object.entries(group.dataset)) {
      if (skip.has(k)) continue;
      if (v === "" || v == null) continue;
      html += `<div><b>${_formatKey(k)}:</b> ${v}</div>`;
   }
   el.innerHTML = html;
   el.style.display = "block";
}

function _positionTooltip(el, evt) {
   const rect = el.getBoundingClientRect();
   const maxX = window.innerWidth - rect.width - 20;
   const maxY = window.innerHeight - rect.height - 20;
   el.style.left = Math.min(evt.pageX + 15, maxX + window.scrollX) + "px";
   el.style.top  = Math.min(evt.pageY + 15, maxY + window.scrollY) + "px";
}

/* ────────────────────────────────────────────────────────
   Tag search
   ──────────────────────────────────────────────────────── */

function _buildIndex(host) {
   const index = new Map();
   const selectors = ".equipment, .valve, .instrument, .pipe-line, .opc, [data-elem-id]";
   host.querySelectorAll(selectors).forEach((el) => {
      const tag = (el.dataset.tag || el.dataset.lineNumber ||
                       el.dataset.elemId || "").toUpperCase();
      if (!tag) return;
      const category = el.classList.contains("equipment")   ? "equipment"
                             : el.classList.contains("valve")       ? "valve"
                             : el.classList.contains("instrument")  ? "instrument"
                             : el.classList.contains("pipe-line")   ? "pipe"
                             : el.classList.contains("opc")         ? "opc"
                             : (el.dataset.elemType || "unknown");
      index.set(tag, {
         element: el,
         type: (el.dataset.type || el.dataset.elemSubtype || "").toUpperCase(),
         service: el.dataset.service || "",
         category,
      });
   });
   return index;
}

function _searchIndex(index, query) {
   const q = query.trim().toUpperCase();
   if (!q) return [];
   /* Type filter — "type:pump" */
   if (q.startsWith("TYPE:")) {
      const wanted = q.substring(5).trim();
      return [...index].filter(([, v]) => v.type.includes(wanted))
                              .map(([tag, v]) => ({ tag, ...v }));
   }
   /* Wildcard */
   if (q.includes("*")) {
      const rx = new RegExp("^" + q.replace(/\*/g, ".*") + "$");
      return [...index].filter(([tag]) => rx.test(tag))
                              .map(([tag, v]) => ({ tag, ...v }));
   }
   /* Exact + partial (exact first) */
   const exact = [];
   const partial = [];
   for (const [tag, v] of index) {
      if (tag === q)             exact.push({ tag, ...v, exact: true });
      else if (tag.includes(q))  partial.push({ tag, ...v });
   }
   return [...exact, ...partial];
}

function _pulseHighlight(el) {
   el.classList.add("pid-search-highlight");
   setTimeout(() => el.classList.remove("pid-search-highlight"), 3000);
}

function _injectHighlightCSS() {
   if (document.getElementById("pid-search-css")) return;
   const style = document.createElement("style");
   style.id = "pid-search-css";
   style.textContent = `
      /* Every interactive P&ID element must catch pointer events on its
       * entire bounding box (otherwise SVG only fires on the exact stroke
       * pixel, which is a nightmare for thin 0.7 mm lines).  This is the
       * single most important CSS rule for the hover / click experience. */
      .pid-elem,
      .equipment,
      .valve,
      .instrument,
      .pipe-line,
      .opc,
      [data-elem-id] {
         pointer-events: bounding-box;
         cursor: pointer;
      }
      /* On hover, tint the whole shape orange + drop-shadow so the user
       * sees exactly what they're pointing at while the tooltip renders. */
      .pid-elem:hover,
      .equipment:hover,
      .valve:hover,
      .instrument:hover,
      .pipe-line:hover,
      [data-elem-id]:hover {
         filter: drop-shadow(0 0 4px #f60);
         outline: none;
      }
      /* Selected element (last-clicked) gets a persistent halo. */
      .pid-selected,
      .pid-elem.pid-selected {
         filter: drop-shadow(0 0 6px #06c) drop-shadow(0 0 2px #06c);
      }
      /* Search pulse — Ctrl+F "found" animation. */
      .pid-search-highlight {
         animation: pid-search-pulse 1s ease-in-out 3;
      }
      @keyframes pid-search-pulse {
         0%,100% { filter: drop-shadow(0 0 0 #f60); }
         50%     { filter: drop-shadow(0 0 8px #f60); }
      }
      /* OPC cross-sheet arrow. */
      .opc { cursor: pointer; }
      .opc:hover { filter: drop-shadow(0 0 3px #06c); }
   `;
   document.head.appendChild(style);
}

function _createSearchBox(host, onResult) {
   let box = document.getElementById(SEARCHBOX_ID);
   if (box) return box;
   box = document.createElement("div");
   box.id = SEARCHBOX_ID;
   box.setAttribute("data-testid", "pid-search-bar");
   box.style.cssText = [
      "position:fixed", "top:70px", "right:30px",
      "background:#fff", "border:1px solid #333", "border-radius:6px",
      "padding:10px 12px",
      "font:13px 'Segoe UI',Arial,sans-serif",
      "box-shadow:0 4px 14px rgba(0,0,0,0.2)",
      "z-index:9998", "min-width:320px", "display:none",
   ].join(";");
   box.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
         <input id="pid-search-input" data-testid="pid-search-input"
                  type="text" placeholder="Tag or line number… (P-*, LT-*, type:pump)"
                  style="border:1px solid #ccc;border-radius:4px;padding:6px 8px;flex:1;outline:none"/>
         <button id="pid-search-close" data-testid="pid-search-close"
                  style="border:none;background:transparent;cursor:pointer;font-size:16px">×</button>
      </div>
      <div id="pid-search-results" data-testid="pid-search-results"
              style="max-height:260px;overflow-y:auto"></div>`;
   document.body.appendChild(box);

   const input = box.querySelector("#pid-search-input");
   const results = box.querySelector("#pid-search-results");
   const close = box.querySelector("#pid-search-close");
   const index = _buildIndex(host);

   const rerender = () => {
      const hits = _searchIndex(index, input.value).slice(0, 25);
      if (hits.length === 0) {
         results.innerHTML = `<div style="color:#888;padding:6px">No matches</div>`;
         return;
      }
      results.innerHTML = hits.map((r, i) => `
         <div data-testid="pid-search-result-${i}"
                data-tag="${r.tag}"
                style="padding:6px 8px;border-bottom:1px solid #eee;cursor:pointer">
            <b>${r.tag}</b>
            <span style="color:#666"> · ${r.category}${r.type ? " · " + r.type : ""}</span>
         </div>`).join("");
      [...results.children].forEach((row) => {
         row.addEventListener("click", () => {
            const hit = hits.find((h) => h.tag === row.dataset.tag);
            if (hit) {
               _pulseHighlight(hit.element);
               onResult && onResult(hit);
               hit.element.scrollIntoView({ behavior: "smooth",
                                                       block: "center" });
            }
         });
      });
   };
   input.addEventListener("input", rerender);
   close.addEventListener("click", () => (box.style.display = "none"));
   box._show = () => {
      box.style.display = "block";
      input.focus();
      input.select();
      rerender();
   };
   return box;
}

/* ────────────────────────────────────────────────────────
   OPC click navigation
   ──────────────────────────────────────────────────────── */

function _wireOpcClicks(host, onNavigate) {
   const handlers = [];
   host.querySelectorAll(".opc, [data-elem-type='opc']").forEach((el) => {
      const handler = (evt) => {
         evt.stopPropagation();
         const info = {
            opcId:       el.dataset.opcId || el.dataset.opcLetter || "",
            letter:      el.dataset.opcLetter || "",
            lineNumber: el.dataset.lineNumber || "",
            destSheet:  el.dataset.destSheet || "",
            sourceSheet: el.dataset.sourceSheet || "",
            direction:   el.dataset.direction || "outgoing",
         };
         onNavigate && onNavigate(info);
      };
      el.addEventListener("click", handler);
      el.style.cursor = "pointer";
      handlers.push([el, handler]);
   });
   return () => handlers.forEach(([el, h]) => el.removeEventListener("click", h));
}

/* ────────────────────────────────────────────────────────
   Main entry — attachPidInteractive
   ──────────────────────────────────────────────────────── */

export function attachPidInteractive(host, opts = {}) {
   if (!host) return () => {};
   _injectHighlightCSS();
   const tooltip = _createTooltip();
   const searchBox = _createSearchBox(host, opts.onSearchResult);

   /* Hover tooltip on interactive elements. */
   const selector = ".equipment, .valve, .instrument, .pipe-line, .opc, [data-elem-id]";
   const nodes = host.querySelectorAll(selector);
   const handlers = [];
   nodes.forEach((el) => {
      const enter = (evt) => _renderTooltip(tooltip, el);
      const move  = (evt) => _positionTooltip(tooltip, evt);
      const leave = () => (tooltip.style.display = "none");
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mousemove", move);
      el.addEventListener("mouseleave", leave);
      handlers.push([el, "mouseenter", enter], [el, "mousemove", move],
                          [el, "mouseleave", leave]);
   });

   /* Global keyboard shortcuts (DOC 8 §8). */
   const keyHandler = (evt) => {
      if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === "f") {
         evt.preventDefault();
         searchBox._show();
      } else if (evt.key === "Escape") {
         searchBox.style.display = "none";
         tooltip.style.display = "none";
      }
   };
   document.addEventListener("keydown", keyHandler);

   /* OPC click navigation. */
   const unwireOpc = _wireOpcClicks(host, opts.onOpcNavigate);

   /* Feb 2026 — Metadata-layer hover reveal.  Stream-annotation
      boxes (S-AUTO-*, S-CW-*, S-IA-*) are CSS-hidden by default.
      Reveal the metadata group associated with the currently
      hovered pipe / equipment. */
   const metaHandlers = [];
   const pipeSelector = ".pipe-line, [data-elem-type='pipe']";
   host.querySelectorAll(pipeSelector).forEach((pipeEl) => {
      const lineNo = pipeEl.getAttribute("data-elem-id")
                          || pipeEl.getAttribute("data-line-number");
      if (!lineNo) return;
      const enter = () => {
         host.querySelectorAll(
            `.metadata-layer[data-pipe="${lineNo}"]`).forEach((g) => {
               g.classList.add("show");
            });
      };
      const leave = () => {
         host.querySelectorAll(
            `.metadata-layer[data-pipe="${lineNo}"]`).forEach((g) => {
               g.classList.remove("show");
            });
      };
      pipeEl.addEventListener("mouseenter", enter);
      pipeEl.addEventListener("mouseleave", leave);
      metaHandlers.push([pipeEl, "mouseenter", enter],
                                [pipeEl, "mouseleave", leave]);
   });

   /* Cleanup. */
   return () => {
      handlers.forEach(([el, ev, h]) => el.removeEventListener(ev, h));
      metaHandlers.forEach(([el, ev, h]) => el.removeEventListener(ev, h));
      document.removeEventListener("keydown", keyHandler);
      unwireOpc();
      tooltip.style.display = "none";
   };
}

/* Convenience — open search box programmatically. */
export function openPidSearch() {
   const box = document.getElementById(SEARCHBOX_ID);
   if (box && box._show) box._show();
}

export default attachPidInteractive;
